import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { UPDATE_USER_LANGUAGE } from '../graphql/mutations';
import { useAuthStore } from '../store/authStore';
import { SupportedLanguage } from '../i18n/config';

export interface UseLanguagePreferenceReturn {
  currentLanguage: SupportedLanguage;
  updateLanguagePreference: (lang: SupportedLanguage) => Promise<void>;
  isUpdating: boolean;
  error: Error | null;
}

export function useLanguagePreference(): UseLanguagePreferenceReturn {
  const { i18n } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const [updateUserLanguage] = useMutation(UPDATE_USER_LANGUAGE);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load saved language preference on mount
  useEffect(() => {
    if (user?.languagePreference) {
      i18n.changeLanguage(user.languagePreference);
    }
  }, [user?.languagePreference, i18n]);

  const updateLanguagePreference = async (lang: SupportedLanguage) => {
    const previousLanguage = i18n.language as SupportedLanguage;
    setIsUpdating(true);
    setError(null);

    try {
      // Update i18n immediately for responsive UI
      await i18n.changeLanguage(lang);

      // If user is logged in, update backend
      if (user) {
        await updateUserLanguage({
          variables: {
            input: {
              userId: user.userId,
              languagePreference: lang
            }
          }
        });

        // Update local state
        updateUser({ ...user, languagePreference: lang });
      }
      // For guest users, language is automatically saved to localStorage by i18next-browser-languagedetector
    } catch (err) {
      setError(err as Error);
      // Revert language change on error
      await i18n.changeLanguage(previousLanguage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    currentLanguage: i18n.language as SupportedLanguage,
    updateLanguagePreference,
    isUpdating,
    error
  };
}
